import type { Middleware } from '@reduxjs/toolkit'
import { logger } from '@tunasong/models'
import { getSessionUserId } from '../auth/jwt.js'
import { createMutationQueue } from './mutation-queue.js'
import { isQueueableEndpoint } from './queueable-endpoints.js'
import { isReplayRequest } from './replay-marker.js'

interface RejectedMutationMeta {
  arg?: { endpointName?: string; originalArgs?: unknown }
  requestStatus?: 'rejected' | 'fulfilled' | 'pending'
  requestId?: string
}

interface RejectedMutationAction {
  type: string
  meta?: RejectedMutationMeta
  payload?: { status?: number | string }
}

const isFailedMutation = (action: unknown): action is RejectedMutationAction => {
  if (!action || typeof action !== 'object') {
    return false
  }
  const a = action as RejectedMutationAction
  return (
    typeof a.type === 'string' && a.type.endsWith('/executeMutation/rejected') && a.meta?.requestStatus === 'rejected'
  )
}

const isRetriableFailure = (status: number | string | undefined): boolean => {
  if (status === 'FETCH_ERROR') {
    return true
  }
  if (typeof status === 'number' && status >= 500) {
    return true
  }
  return false
}

// Lazily-constructed queue so unit tests that swap globalThis.indexedDB in
// beforeEach get a fresh underlying DB instance per case.
let queueInstance: ReturnType<typeof createMutationQueue> | null = null
const queue = () => {
  if (!queueInstance) {
    queueInstance = createMutationQueue()
  }
  return queueInstance
}
/** Test-only: reset the memoised queue so beforeEach can install a clean factory. */
export const resetMutationQueueForTests = () => {
  queueInstance = null
}

export const mutationQueueMiddleware: Middleware = _store => next => action => {
  const result = next(action)
  if (!isFailedMutation(action)) {
    return result
  }
  // A failed REPLAY must never be re-enqueued as a fresh item — attempts must
  // accumulate on the original queued item via replayMutationQueue, otherwise a
  // mutation failing with a retriable 5xx spawns an attempts=0 clone on every
  // reconnect and loops forever.
  if (isReplayRequest(action.meta?.requestId)) {
    return result
  }
  const endpointName = action.meta?.arg?.endpointName
  if (!endpointName || !isQueueableEndpoint(endpointName)) {
    return result
  }
  if (!isRetriableFailure(action.payload?.status)) {
    return result
  }
  const apiName = action.type.split('/')[0]
  // TODO(offline-hardening Phase 2): capture ETag from
  // state.entitiesApi.queries[`loadEntity(${id})`].data?.eTag so replay sends
  // If-Match. Until then conflict detection (412 → dead-letter) is only
  // reachable via test-driven dispatches, not from real production traffic.
  // See Pre-flight P.3 finding in the offline-hardening spec.
  void queue()
    .enqueue({
      endpointName,
      apiName,
      args: action.meta?.arg?.originalArgs,
      etag: null,
      userId: getSessionUserId(),
    })
    .catch(e => logger.error('mutationQueueMiddleware: enqueue failed', e))
  return result
}
